(() => {
  const root = document.documentElement;
  const announcementSelector = '#header-group .announcement-bar';
  const progressSelector = '#header-group [class*="ai-free-shipping-bar-"]';

  let announcement = null;
  let progress = null;
  let announcementResizeObserver = null;
  let progressResizeObserver = null;
  let announcementMutationObserver = null;
  let progressMutationObserver = null;
  let headerObserver = null;

  const isVisible = (element) => {
    if (!element || !element.isConnected) return false;
    const styles = window.getComputedStyle(element);
    return (
      styles.display !== 'none' &&
      styles.visibility !== 'hidden' &&
      element.getClientRects().length > 0
    );
  };

  const setVariable = (name, height) => {
    root.style.setProperty(name, `${Math.max(0, Math.ceil(height))}px`);
  };

  const measure = () => {
    const nextAnnouncement = document.querySelector(announcementSelector);
    const nextProgress = document.querySelector(progressSelector);

    if (nextAnnouncement !== announcement) attachAnnouncement(nextAnnouncement);
    if (nextProgress !== progress) attachProgress(nextProgress);

    const announcementHeight = isVisible(announcement)
      ? announcement.getBoundingClientRect().height
      : 0;
    const progressHeight = isVisible(progress)
      ? progress.getBoundingClientRect().height
      : 0;

    setVariable('--ageless-announcement-height', announcementHeight);
    setVariable('--ageless-free-shipping-height', progressHeight);
  };

  const attachAnnouncement = (element) => {
    announcementResizeObserver?.disconnect();
    announcementMutationObserver?.disconnect();
    announcement = element;

    if (!announcement) return;

    if ('ResizeObserver' in window) {
      announcementResizeObserver = new ResizeObserver(() => requestAnimationFrame(measure));
      announcementResizeObserver.observe(announcement);
    }

    announcementMutationObserver = new MutationObserver(() => requestAnimationFrame(measure));
    announcementMutationObserver.observe(announcement, {
      attributes: true,
      childList: true,
      subtree: true
    });
  };

  const attachProgress = (element) => {
    progressResizeObserver?.disconnect();
    progressMutationObserver?.disconnect();
    progress = element;

    if (!progress) return;

    if ('ResizeObserver' in window) {
      progressResizeObserver = new ResizeObserver(() => requestAnimationFrame(measure));
      progressResizeObserver.observe(progress);
    }

    progressMutationObserver = new MutationObserver(() => requestAnimationFrame(measure));
    progressMutationObserver.observe(progress, {
      attributes: true,
      attributeFilter: ['style', 'class'],
      childList: true,
      subtree: true
    });
  };

  /*
   * The product-template branch that used to live here rewrote the PDP
   * reassurance paragraph in JavaScript, and its hardcoded copy still read
   * "30-day claim window for damaged, defective, or incorrect orders" —
   * the pre-guarantee wording. It was inert once the trust row moved into
   * snippets/text.liquid, but it would have silently reinstated copy that
   * contradicts the 30-day money-back guarantee if that block ever changed.
   *
   * The PDP trust row is now rendered server-side in snippets/text.liquid.
   * Edit it there. Do not reintroduce a JS rewrite of product trust copy.
   */
  const applyGrowthEnhancements = () => {
    const main = document.getElementById('MainContent');
    if (!main) return;

    const template = String(main.dataset.template || '').toLowerCase();

    if (template === 'index') {
      const heroCta = Array.from(main.querySelectorAll('a')).find((link) => {
        const label = link.textContent.trim().toLowerCase();
        const href = link.getAttribute('href') || '';
        return label === 'shop featured formulas' || href.includes('/collections/best-sellers');
      });

      if (heroCta && !heroCta.dataset.agelessGrowthCta) {
        heroCta.textContent = 'Shop the Longevity Starter Stack';
        heroCta.setAttribute('href', '/products/longevity-starter-stack-1');
        heroCta.dataset.agelessGrowthCta = 'true';
      }
    }
  };

  const initialize = () => {
    attachAnnouncement(document.querySelector(announcementSelector));
    attachProgress(document.querySelector(progressSelector));
    measure();
    applyGrowthEnhancements();

    const headerGroup = document.getElementById('header-group');
    if (headerGroup) {
      headerObserver?.disconnect();
      headerObserver = new MutationObserver(() => requestAnimationFrame(measure));
      headerObserver.observe(headerGroup, { childList: true, subtree: true });
    }

    requestAnimationFrame(() => {
      measure();
      applyGrowthEnhancements();
    });
    window.setTimeout(() => {
      measure();
      applyGrowthEnhancements();
    }, 250);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  window.addEventListener('resize', () => requestAnimationFrame(measure), { passive: true });
  window.addEventListener('load', () => {
    measure();
    applyGrowthEnhancements();
  }, { once: true });
  document.addEventListener('cart:update', () => requestAnimationFrame(measure));
  document.addEventListener('cart:updated', () => requestAnimationFrame(measure));
  document.addEventListener('cart:refresh', () => requestAnimationFrame(measure));
  document.addEventListener('shopify:section:load', () => requestAnimationFrame(initialize));
})();
